/* eslint-disable react-hooks/exhaustive-deps */
import { Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import ConfigurableTable from 'components/shared/table/ConfigurableTable';
import {
  PAYER_MIX_COLUMNS,
  ColumnVisiblity,
  PAGINATION,
  PayerMixField,
  renderTitleTooltip,
  SORT_BY,
  TABLE_NAME_PAYER_MIX,
  CUSTOMER_TYPE,
  PayerMixDataKeyFieldMapping,
  CUSTOMER_TYPE_ENTITY,
  POLICY_STATUS_COL_POPOVER_LINK,
  POLICY_STATUS_COL_POPOVER_DESC,
  QUERY_KEY,
  ACCOUNT_TYPE,
  COMPONENT_MAPPING,
  MONTH_FULL_YEAR_FORMAT,
} from 'constants/constants';
import {
  exportTableData,
  getPayerTableData,
  getPayerTimeSpanDates,
} from 'store/customer/customer-view-actions';
import { getRestrictedColumn, formatDate } from 'utils/utils';
import classes from './PayerMix.module.scss';

import { Checkbox, Popover } from 'antd';
import PayerMixFilter from './PayerMixFilter';
import { capitalize } from 'lodash';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';
import {
  setBenifitType,
  setBookofBusiness,
  setCompetitor,
  setParentPayer,
  setPayerEcosystem,
  setPayerIndication,
  setPayerMixFilterInitailState,
  setPayerMixPayer,
  setPayerProduct,
} from 'store/customer/customerViewSlice';
import OverlayIcon from 'components/shared/overlay-icon/OverlayIcon';
import PopoverAsTooltip from 'components/customer/popover/PopoverAsTooltip';
import { useCustomerDetails } from 'components/customer/banner/Banner.hooks';
import { useQuery } from '@tanstack/react-query';

const PayerMix = () => {
  const activeSectionTab = useSelector(
    (state) => state?.CustomerView?.activeSectionTab
  );
  const selectedParentPayer = useSelector(
    (state) => state.CustomerView?.payerMixFilters?.Parent_Payer.selectedValue
  );
  const selectedPayer = useSelector(
    (state) => state.CustomerView?.payerMixFilters?.Payer.selectedValue
  );
  const selectedProduct = useSelector(
    (state) =>
      state.CustomerView?.payerMixFilters?.Payer_Mix_Product.selectedValue
  );
  const selectedBenifitType = useSelector(
    (state) => state.CustomerView?.payerMixFilters?.Benifit_Type.selectedValue
  );
  const selectedIndication = useSelector(
    (state) =>
      state.CustomerView?.payerMixFilters?.Payer_Indication.selectedValue
  );
  const selectedEcosystem = useSelector(
    (state) =>
      state.CustomerView?.payerMixFilters?.Payer_Ecosystem.selectedValue
  );
  const selectedBookOfBusiness = useSelector(
    (state) =>
      state.CustomerView?.payerMixFilters?.Book_Of_Business.selectedValue
  );
  const selectedCompetitorBrand = useSelector(
    (state) =>
      state.CustomerView?.payerMixFilters?.Competitor_Brand.selectedValue
  );
  const [popoverVisible, setPopoverVisible] = useState(false);

  const dispatch = useDispatch();

  const isExporting = useSelector(
    (state) => state?.CustomerView?.exportAccountpayermix
  );
  const columnRestriction = useSelector(
    (state) => state?.CustomerView?.columnsRestiction
  );
  const selectedView = useSelector(
    (state) => state?.CustomerView?.selectedView
  );
  const selectedCustomer = useSelector(
    (state) => state?.CustomerView?.selectedCustomer
  );
  const { data: accountDetails } = useCustomerDetails(selectedCustomer);
  const { roleId } = useSelector((state) => state?.Auth?.userClaimObject);

  const dimensions = [
    'ParentPayerName',
    'PayerName',
    'BookOfBusiness',
    'BenefitType',
  ];

  // Remove restricted columns as per role and show in table
  const payerMixColumns = useMemo(
    () =>
      getRestrictedColumn(
        TABLE_NAME_PAYER_MIX,
        PAYER_MIX_COLUMNS,
        columnRestriction
      ),
    []
  );
  // Get Default selected columns as per configuration and show in column setting popover
  const getDefaultSelectedColumns = () => {
    return payerMixColumns.reduce((result, column) => {
      if (
        column.isMandatory === 'Y' &&
        column.columnVisiblity !== ColumnVisiblity.Optional &&
        column.key
      ) {
        result.push(column.key);
      }
      return result;
    }, []);
  };

  const [selectedColumns, setSelectedColumns] = useState(
    getDefaultSelectedColumns()
  );

  useEffect(() => {
    setSelectedColumns(getDefaultSelectedColumns());
  }, [selectedCustomer?.value]);

  // Filter selected columns to show in table as per selected columns from setting popover
  const filteredColumns = payerMixColumns.filter((column) =>
    selectedColumns.includes(column.key)
  );

  // React Query to fetch PayerMix TimeSpan Data
  const { data: payerTimeSpan } = useQuery({
    queryKey: [QUERY_KEY.PayerMixTimePeriod, roleId],
    queryFn: async () => {
      const response = await getPayerTimeSpanDates({ roleId });
      return response;
    },
    enabled: activeSectionTab === COMPONENT_MAPPING.PayerMix ? true : false,
  });

  let defaultSortedColumn = 'payerContributionToAccount';
  const defaultPageSize = PAGINATION.defaultPageSize;
  const initailState = {
    values: [],
    offset: 0,
    totalSize: 0,
  };

  const [payerMiXData, setPayerMiXData] = useState(initailState);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortBy, setSortBy] = useState({
    column: defaultSortedColumn,
    order: SORT_BY.descend,
  });

  const pagination = {
    current: pageIndex,
    total: payerMiXData?.totalSize,
    pageSize: pageSize,
    pageSizeOptions: PAGINATION.pageSizeOptions,
    showSizeChanger: true,
    disabledPageSizehandler: true,
  };

  // React Query to fetch PayerMix Data
  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.PayerMixTable,
      selectedParentPayer,
      selectedPayer,
      selectedBookOfBusiness,
      selectedBenifitType,
      selectedProduct,
      selectedIndication,
      selectedCompetitorBrand,
      selectedEcosystem,
      selectedColumns,
      selectedCustomer?.value,
      sortBy.column,
      sortBy.order,
      pageIndex,
      pageSize,
    ],
    queryFn: async () => {
      const result = await getPayerData();
      return result?.values?.length > 0 ? result : initailState;
    },
    enabled: activeSectionTab === COMPONENT_MAPPING.PayerMix ? true : false,
  });

  // Update local state when query data changes
  useEffect(() => {
    if (data) {
      setPayerMiXData(data);
    }
  }, [data]);

  const resetFilters = () => {
    dispatch(setPayerMixFilterInitailState());
  };

  const getSortedSelectedColumn = () => {
    return PAYER_MIX_COLUMNS?.filter((col) =>
      selectedColumns?.includes(col.key)
    ).map((col) => col.key);
  };

  const getPayerData = async () => {
    try {
      // Calculate offset - will be 0 when pageIndex is 1
      let offset = Math.max(0, (pageIndex - 1) * pageSize);

      // Build payload for API request
      const payload = {
        mdmId: selectedCustomer?.value,
        entityType: selectedCustomer?.flag,
        sortByColumn: selectedColumns?.includes(
          PayerMixDataKeyFieldMapping[sortBy.column]
        )
          ? PayerMixDataKeyFieldMapping[sortBy.column]
          : undefined,
        sortByDirection: selectedColumns?.includes(
          PayerMixDataKeyFieldMapping[sortBy.column]
        )
          ? sortBy.order
          : undefined,
        dimension: dimensions,
        offset,
        pageSize,
        requestType: 'PayerMix',
        selectedColumns: getSortedSelectedColumn(),
        parentPayerId: selectedParentPayer?.map((item) => item.value),
        payerId: selectedPayer?.map((item) => item.value),
        product: selectedProduct?.value,
        indication: selectedIndication?.value,
        competitorProduct: selectedCompetitorBrand?.value,
        ecosystemName: selectedEcosystem?.map((item) => item.value),
        bookOfBusiness: selectedBookOfBusiness?.map((item) => item.value),
        benefitType: selectedBenifitType?.map((item) => item.value),
        roleId: roleId,
      };

      // Make API call
      const result = await getPayerTableData({ ...payload });
      return result;
    } catch (error) {
      console.log(error);
    }
  };

  const handleTableChange = (column, order, pagination) => {
    let currentPageSize = pagination.pageSize;

    // Reset page to 1 if page size changed
    if (pageSize !== currentPageSize) {
      setPageIndex(1);
      setPageSize(currentPageSize);
    } else {
      setPageIndex(pagination.current);
    }

    setSortBy({ column, order: SORT_BY[order] });
  };

  const downloadData = () => {
    let payload = {
      mdmId: selectedCustomer?.value,
      entityType: selectedCustomer?.flag,
      sortByColumn: selectedColumns?.includes(
        PayerMixDataKeyFieldMapping[sortBy.column]
      )
        ? PayerMixDataKeyFieldMapping[sortBy.column]
        : undefined,
      sortByDirection: selectedColumns?.includes(
        PayerMixDataKeyFieldMapping[sortBy.column]
      )
        ? sortBy.order
        : undefined,
      dimension: dimensions,
      offset: 0,
      pageSize: pageSize,
      requestType: 'PayerMix',
      selectedColumns: getSortedSelectedColumn(),
      parentPayerId: selectedParentPayer?.map((item) => item.value) ?? [],
      payerId: selectedPayer?.map((item) => item.value) ?? [],
      product: selectedProduct?.value ?? undefined,
      indication: selectedIndication?.value ?? undefined,
      competitorProduct: selectedCompetitorBrand?.value ?? undefined,
      ecosystemName: selectedEcosystem?.map((item) => item.value) ?? [],
      bookOfBusiness: selectedBookOfBusiness?.map((item) => item.value) ?? [],
      benefitType: selectedBenifitType?.map((item) => item.value) ?? [],
      roleId: roleId,
    };

    const fileName = `${capitalize(CUSTOMER_TYPE.get(selectedCustomer?.flag))}-[${selectedCustomer?.label}-${accountDetails?.iqviaId}]-PayerMix`;
    dispatch(exportTableData('AccountPayerMix', fileName, payload));
  };

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    const nextSelected = checked
      ? [value, ...selectedColumns]
      : selectedColumns.filter((item) => item !== value);
    setSelectedColumns(nextSelected);
  };

  const handlePopoverVisibleChange = (visible) => {
    setPopoverVisible(visible);
  };

  const handleFilterChange = (value, field) => {
    if (field === PayerMixField.ParentPayer) {
      dispatch(setParentPayer({ selectedValue: value }));
    } else if (field === PayerMixField.Payer) {
      dispatch(setPayerMixPayer({ selectedValue: value }));
    } else if (field === PayerMixField.BookOfBusiness) {
      dispatch(setBookofBusiness({ selectedValue: value }));
    } else if (field === PayerMixField.BenefitType) {
      dispatch(setBenifitType({ selectedValue: value }));
    } else if (field === PayerMixField.Product) {
      dispatch(setPayerProduct({ selectedValue: value }));
      if (!value) {
        dispatch(setPayerIndication({ selectedValue: null }));
        dispatch(setCompetitor({ selectedValue: null }));
      }
    } else if (field === PayerMixField.Indication) {
      dispatch(setPayerIndication({ selectedValue: value }));
      if (!value) {
        dispatch(setCompetitor({ selectedValue: null }));
      }
    } else if (field === PayerMixField.CompetitorProduct) {
      dispatch(setCompetitor({ selectedValue: value }));
    } else if (field === PayerMixField.EcosystemName) {
      dispatch(setPayerEcosystem({ selectedValue: value }));
    }
    setPageIndex(1);
  };

  const exportTooltip = (
    <Tooltip className='gne-tooltip'>Send to Excel</Tooltip>
  );

  const size = { x: 1100, y: 400 };

  const payerMixTooltip = (
    <Tooltip className='gne-tooltip'>
      <span>
        Sales Data (Plantrak and Imputed Sales) is latest as of{' '}
        {formatDate(payerTimeSpan?.salesDataAsOf)} (R12M)
      </span>
      <br />
      <span>
        Lives Data (DRG) is latest as of{' '}
        {formatDate(payerTimeSpan?.livesDataAsOf)}
      </span>
      <br />
      <span>
        Policy Data (MMIT) is latest as of{' '}
        {formatDate(payerTimeSpan?.policyDataAsOf)}
      </span>
    </Tooltip>
  );

  const renderPopOver = (title, info, link) => {
    return (
      <>
        <span>{title}</span>
        <PopoverAsTooltip>
          <span>{info}</span>
          <a
            href={link}
            target='_blank'
            rel='noreferrer'
            onClick={(e) => {
              e.stopPropagation();
            }}>
            &nbsp; <span className='document_link'>link</span>
          </a>
          .
        </PopoverAsTooltip>
      </>
    );
  };

  return (
    <div id='PayerMix'>
      <Row>
        <Col
          className='d-flex align-items-center'
          md={6}>
          <p className='title title__bold title__large mb-1'>
            Payer Mix
            <OverlayTrigger overlay={payerMixTooltip}>
              <i className='icon icon__info__gray ms-2'></i>
            </OverlayTrigger>
          </p>
        </Col>
        <Col
          className='d-flex justify-content-end align-items-center '
          md={6}>
          <Popover
            content={
              <div className={classes.acc_custom_popup}>
                <ColumnSelectionPanel
                  classes={classes}
                  selectedColumns={selectedColumns}
                  handleCheckboxChange={handleCheckboxChange}
                  columns={payerMixColumns}
                  selectedView={selectedView}
                />
              </div>
            }
            trigger='click'
            open={popoverVisible}
            onOpenChange={handlePopoverVisibleChange}>
            <i
              className={`icon icon__affiliations_standardTable_settings me-3`}
            />
          </Popover>
          <OverlayIcon
            tooltip={'Reset Filters'}
            onClick={resetFilters}
            className={`icon icon__reset-filters me-3`}
          />
          <OverlayTrigger overlay={exportTooltip}>
            <i
              onClick={downloadData}
              className={`icon icon__affiliations_export_excel1 `}
            />
          </OverlayTrigger>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <div className='title title__x-small pt-0 title__grey d-flex '>
            <p
              className={`title title__x-small title__italic title__grey ${classes['acc-verbiag']}`}>
              Payer mix is based on an "imputed" methodology, leveraging
              zip3-level lives by site and book of business mix for the selected
              product. Some columns are customizable and can be added/removed to
              fit need. | R12M | Data updated through &nbsp;
              {formatDate(
                payerTimeSpan?.dataRefreshDate,
                MONTH_FULL_YEAR_FORMAT
              )}
            </p>
          </div>
        </Col>
      </Row>
      {/* PayerMix Filter Section */}
      <PayerMixFilter
        parentPayer={selectedParentPayer}
        payer={selectedPayer}
        bookOfBusiness={selectedBookOfBusiness}
        benifitType={selectedBenifitType}
        product={selectedProduct}
        indication={selectedIndication}
        competitorBrand={selectedCompetitorBrand}
        ecosystem={selectedEcosystem}
        handleFilterChange={handleFilterChange}
      />
      &nbsp;
      {/* PayerMix Table Section */}
      <Row>
        <Col xs={12}>
          <Spin spinning={isLoading || isExporting}>
            <ConfigurableTable
              rows={payerMiXData?.values}
              columns={filteredColumns?.map((col) => {
                if (col.dataIndex === 'policyUpdateURL') {
                  return {
                    ...col,
                    render: (text, record) => {
                      return (
                        <a
                          target='_blank'
                          rel='noreferrer'
                          className='span-link'
                          href={`${process.env.REACT_APP_POLICY_FEEDBACK_UPDATE_URL}&${record?.policyUpdateURLParams}`}>
                          Policy Intelligence Update Form
                        </a>
                      );
                    },
                  };
                } else if (col.dataIndex === 'policyStatus') {
                  return {
                    ...col,
                    title: renderPopOver(
                      'Policy Status',
                      POLICY_STATUS_COL_POPOVER_DESC,
                      POLICY_STATUS_COL_POPOVER_LINK
                    ),
                  };
                } else if (col.dataIndex === 'payerContributionToAccount') {
                  return {
                    ...col,
                    ...(selectedView === ACCOUNT_TYPE.site && {
                      label: 'Estimated Contribution to Site (%)',
                      title: renderTitleTooltip(
                        'Estimated Contribution to Site (%)',
                        "This metric shows the Payer's contribution to the Site imputed sales over R12M. This metric is affected by Product, Book of Business, Benefit Type, and Ecosystem. This metric is NOT affected by Indication."
                      ),
                    }),
                  };
                }
                return col;
              })}
              expandRowByClick={true}
              enableColumnSelection={false}
              enableExport={false}
              exportFileName='PayerMixDetails'
              tableName={TABLE_NAME_PAYER_MIX}
              isGlobalExport={true}
              pagination={pagination}
              isGlobalSort={true}
              onSort={handleTableChange}
              dimentions={size}
            />
          </Spin>
        </Col>
      </Row>
    </div>
  );
};

export default PayerMix;

const ColumnSelectionPanel = ({
  classes,
  selectedColumns,
  handleCheckboxChange,
  columns,
  selectedView,
}) => {
  return (
    <Checkbox.Group
      defaultValue={selectedColumns}
      className={classes.checkboxGroup}
      key={Math.random()}>
      {columns.map((column) => (
        <div
          className={classes.checkboxWrapper}
          key={column.id}>
          <Checkbox
            className={`${classes.checkbox}`}
            key={column.key}
            value={column.key}
            disabled={column.columnVisiblity === ColumnVisiblity?.Mandatory}
            onChange={handleCheckboxChange}>
            <div className={`${classes['truncate-text']}`}>
              {selectedView === CUSTOMER_TYPE_ENTITY.SITE &&
              column.dataIndex === 'payerContributionToAccount'
                ? 'Estimated Contribution to Site (%)'
                : column.label
                  ? column.label
                  : column.title}
            </div>
          </Checkbox>
        </div>
      ))}
    </Checkbox.Group>
  );
};
